import { useState, useCallback } from 'react'
import { AlertCircle } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { DEFAULT_PAGE_SIZE, ListPagination } from '@/components/common/ListPagination'
import { OrderFilterBar } from './components/OrderFilterBar'
import { OrderTable } from './components/OrderTable'
import { OrderDetailDrawer } from './components/OrderDetailDrawer'
import { useEmployeeOrders } from '@/hooks/useEmployeeOrder'
import type { EmployeeOrderListParams } from '@/types/employee'

export function EmployeeOrdersPage() {
  const [params, setParams] = useState<EmployeeOrderListParams>({
    page: 1,
    page_size: DEFAULT_PAGE_SIZE,
  })
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  const { data, isLoading, error } = useEmployeeOrders(params)

  const handleParamsChange = useCallback((newParams: Partial<EmployeeOrderListParams>) => {
    setParams((prev) => ({
      ...prev,
      ...newParams,
      page: 1,
    }))
  }, [])

  const handlePageChange = useCallback((page: number) => {
    setParams((prev) => ({ ...prev, page }))
  }, [])

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setParams((prev) => ({ ...prev, page: 1, page_size: pageSize }))
  }, [])

  const handleSelectOrder = useCallback((orderId: number) => {
    setSelectedOrderId(orderId)
    setDrawerOpen(true)
  }, [])

  const handleCloseDrawer = useCallback(() => {
    setDrawerOpen(false)
    setSelectedOrderId(null)
  }, [])

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-bold tracking-tight">我的订单</h1>
          <p className="text-muted-foreground">查看您关联的订单和佣金信息</p>
        </div>
      </div>

      {/* 错误提示 */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error instanceof Error ? error.message : '加载数据失败，请稍后重试'}
          </AlertDescription>
        </Alert>
      )}

      {/* 筛选栏 */}
      <OrderFilterBar params={params} onParamsChange={handleParamsChange} />

      {/* 订单列表 */}
      <OrderTable
        orders={data?.list || []}
        isLoading={isLoading}
        onSelectOrder={handleSelectOrder}
      />

      {/* 分页 */}
      <ListPagination
        page={params.page}
        pageSize={params.page_size}
        total={data?.total || 0}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      {/* 详情抽屉 */}
      <OrderDetailDrawer
        orderId={selectedOrderId}
        open={drawerOpen}
        onClose={handleCloseDrawer}
      />
    </div>
  )
}
